import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormState } from "react-hook-form";
import { useEffect, useState } from "react";

// Types
import PagerDutyType from "../../../../types/integration/IntegrationType";

// Constants.
import {
    ACTIONS,
    generalErrorMessage,
    pagerDutyAttributesArray
} from '../../../../component/Constants';

//Utils
import { getAction, getValidClass } from "../../../../helpers/utils";
import Action from "../../../../types/Action";
import api from "../../../../api/Base";
import Alert from "../../../../component/Alert";
import IntegrationsApi from "../../../../api/Integrations";
import useIntegrationTest from "../../../../hooks/useIntegrationTest";
import {
    findProfile,
    getNextProfilePrefix,
    getProfileValue,
    NOTIFICATION_INTEGRATIONS
} from "../../../../helpers/notificationIntegrations";

function Detail(props: { pagerDuty: PagerDutyType[], refresh: Function, isAdmin: boolean }) {
    const { actionId, profileId } = useParams();
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
    const [hasChanged, setHasChanged] = useState(false);
    const [apikeyElementType, setApiKeyElementType] = useState<string>('password');
    const navigate = useNavigate();
    const config = NOTIFICATION_INTEGRATIONS.pagerduty;

    // Redirect non-admin users
    useEffect(() => {
        if (!props.isAdmin) {
            navigate('/admin/integrations/pager-duty/');
        }
    }, [props.isAdmin, navigate]);
    const isDelete = action.type === ACTIONS.DELETE.type;
    const {
        register,
        trigger,
        handleSubmit,
        getValues,
        setValue,
        control,
        formState: {errors}
    } = useForm<PagerDutyType>({
        mode: "onBlur",
        reValidateMode: 'onSubmit',

        shouldFocusError: true
    });

    // Handle input change
    const handleChange = (event: any) => {
        const newValue = event.nativeEvent.data
        if (!hasChanged) {
            setHasChanged(true);
            setVisiblePassword(true);
            event.target.value = newValue?.trim();
            setValue('apikey', newValue, { shouldDirty: true });
        }
    };

    useEffect(() => {
        getAction(actionId, setAction, setDisabled);
    }, [actionId]);

    useEffect(() => {
        setVisiblePassword(action === ACTIONS.CREATE)
    }, [action]);

    const [disabled, setDisabled] = useState(isDelete);

    const { TestNowButton, TestResultAlert } = useIntegrationTest({
        onTest: () => {
            const data = getValues();
            const params: { name: string; value: string; encrypted?: boolean }[] = [];
            Object.keys(data).forEach(item => {
                const name = item === 'dbmarurl' ? 'pagerduty.dbmarlin.url' : `pagerduty.${item}`;
                // @ts-ignore
                const value = data[item] === true ? 'true' : data[item] === false ? 'false' : data[item];
                const entry: { name: string; value: string; encrypted?: boolean } = { name, value };
                if (item === 'apikey') entry.encrypted = !hasChanged;
                params.push(entry);
            });
            return IntegrationsApi.testPagerDutyIntegration(params);
        },
        successMessage: 'PagerDuty test notification sent successfully.',
        errorMessage: 'Test failed. Please check your PagerDuty settings.',
    });

    const parseEditResponse = (data: any) => {
        if (action.type === ACTIONS.CREATE.type) {
            return;
        }
        const profile = findProfile(data, config, action.type === ACTIONS.CREATE.type ? undefined : profileId);
        if (profile) {
            setValue('name', profile.label);
            pagerDutyAttributesArray.forEach(attr => setValue(attr as keyof PagerDutyType, getProfileValue(profile, attr) as any));
            setValue('dbmarurl', getProfileValue(profile, 'dbmarlin.url') as string)
            void trigger()
        }
    }

    useEffect(() => {
        parseEditResponse(props.pagerDuty)
    }, [props.pagerDuty, action.type, profileId]);

    const {dirtyFields} = useFormState({
        control
    })


    const onSubmit = async(data: PagerDutyType) => {
        setDisabled(true)
        const updatedValues: { name: string; value: any; }[] = []
        let result: any;
        let url = `/parameter`;
        const prefix = action.type === ACTIONS.CREATE.type
            ? getNextProfilePrefix(props.pagerDuty, config)
            : profileId || config.basePrefix;

        if (action.type === ACTIONS.DELETE.type) {
            const profile = findProfile(props.pagerDuty, config, prefix);
            result = await api.delete(url, {data: (profile?.parameters ?? []).map(parameter => ({ name: parameter.name }))})
        } else {
            Object.keys(data).forEach(item => {
                let name = (item === 'dbmarurl') ? `${prefix}.dbmarlin.url` : `${prefix}.${item}`
                name = (item === 'name') ? `${prefix}.dbmarlin.name` : name;
                // @ts-ignore
                const value = data[item] === true ? 'true' : data[item] === false ? 'false' : data[item]
                if (action.type === 'create') {
                    updatedValues.push({name, value})
                    // @ts-ignore
                } else if (dirtyFields[item]) {
                    updatedValues.push({name, value})
                }
            })
            result = updatedValues.length > 0 ? await api.post(url, updatedValues) : { status: 200 }
        }

        if (result.status === 200) {
            navigate('/admin/integrations/pager-duty')
            props.refresh()
        }
    }

    const isValid = Object.keys(errors).length === 0

    return (
        <div className="col">
            <div className="card">
                <div className="card-header">
                    <i className="fak fa-pagerduty fa-fw" aria-hidden="true"/>
                    {action.title} PagerDuty
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {!isValid && (
                            <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                   variant="alert-danger"/>
                        )}

                        <p>Please complete the required details below:</p>

                        <div className="row g-0 form">
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="name" className="base-url">Name</label>
                                </span>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="name"
                                    maxLength={250}
                                    {...register('name', { required: "Please fill in this field" })}
                                    className={`form-control ${getValidClass(errors.name, getValues('name'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.name?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.name?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* URL */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">URL</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="url"
                                    maxLength={250}
                                    {...register('url', {
                                        required: "Please fill in this field",
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: "Invalid Url"
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.url, getValues('url'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.url?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.url?.message}</small>

                            </div>
                            <div className="w-100"/>

                            {/* API Key */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">API Key</label>
                                </span>
                                {visiblePassword && (
                                    <i className="fas fa-eye fa-fw fa-xs ms-1" role="button"
                                       onClick={() => ((apikeyElementType === 'text') ? setApiKeyElementType('password') : setApiKeyElementType('text'))}/>
                                )}
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type={apikeyElementType}
                                    id="apikey"
                                    maxLength={250}
                                    {...register("apikey", {required: "Please fill in this field"})}
                                    className={`form-control ${getValidClass(errors.apikey, getValues('apikey'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.apikey?.message}
                                    data-lpignore={true}
                                    onChange={handleChange}
                                />
                                <small className="text-danger">{errors.apikey?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* DBmarlin URL */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">DBmarlin URL</label>
                                </span>
                            </div>

                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <input
                                    type="text"
                                    id="dbmarurl"
                                    maxLength={250}
                                    {...register('dbmarurl', {
                                        required: "Please fill in this field",
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: "Invalid Url"
                                        }
                                    })}
                                    className={`form-control ${getValidClass(errors.dbmarurl, getValues('apikey'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled}
                                    title={errors.dbmarurl?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.dbmarurl?.message}</small>
                            </div>
                            <div className="w-100"/>

                            {/* Enabled */}
                            <div className="col-xs-12 col-md-3">
                                <label className="auth">Enabled</label>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-3">
                                <div className='form-check form-switch'>
                                    <input
                                        type="checkbox"
                                        id="enable"
                                        {...register("enable")}
                                        disabled={disabled}
                                        className="form-check-input"
                                        // onChange={() => setValue('enable', !getValues('enable'))}
                                        defaultChecked={true}
                                        data-lpignore={true}
                                    />
                                </div>
                            </div>
                            <div className="w-100"/>


                            {/* Actions */}
                            <div className="col-12 actions">
                                <Link to="/admin/integrations/pager-duty" role="button" className="btn btn-secondary"><i
                                    className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></Link>
                                {action.type === 'update' && <TestNowButton className="btn btn-info"/>}
                                <button type="submit" className={`btn btn-${action.variant}`}><i
                                    className={`${action.icon} fa-sm`}/><span>{action.title}</span></button>
                            </div>
                            <TestResultAlert className="col-12"/>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Detail;
